import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { mapScenarioViewToScenario } from "../api/mappers/scenarioMapper";
import type { ScenarioViewFilterInput } from "../api/models/scenarioView";
import {
  getScenariosCount,
  getScenariosGraphql,
  type ScenarioViewSortInput,
} from "../api/services/scenarioGraphqlService";
import { useApp } from "../contexts/AppContext";
import type { ComprehensiveScenario } from "../types/scenarios";

export type ScenarioSortBy = "recent" | "name" | "impact";

export interface ScenarioViewParams {
  /** Free-text search — matched against Name + Description server-side. */
  search?: string;
  /** Category pill — 'all' | 'growth' | 'cost-cutting' | 'crisis' | 'custom'. */
  category?: string;
  /** Status dropdown — 'all' or a concrete scenario status. */
  status?: string;
  /** Sort key. Defaults to 'recent' (DateModified desc). */
  sortBy?: ScenarioSortBy;
  /** Max scenarios to fetch. */
  first?: number;
  /** Skip fetching entirely (e.g. tab not visible). */
  enabled?: boolean;
}

export interface ScenarioViewResult {
  /** Ordered scenarios from the server (empty in demo mode). */
  scenarios: ComprehensiveScenario[];
  /** Total matching the filter (server count). */
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  /** True when live mode is active — callers use this to decide the data source. */
  isLive: boolean;
}

const SORT_MAP: Record<ScenarioSortBy, ScenarioViewSortInput> = {
  recent: { field: "DateModified", order: "Descending" },
  name: { field: "Name", order: "Ascending" },
  impact: { field: "NetCashImpact", order: "Descending" },
};

/**
 * Build the combined GraphQL filter from the search box, category pills and
 * status dropdown. All three are AND'd together server-side.
 */
export function buildScenarioViewFilter(
  params: ScenarioViewParams,
): ScenarioViewFilterInput | undefined {
  const filter: ScenarioViewFilterInput = {};

  const search = params.search?.trim();
  if (search) filter.summary = search;

  if (params.category && params.category !== "all") {
    filter.category = { eq: params.category };
  }

  if (params.status && params.status !== "all") {
    filter.status = { eq: params.status };
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

/**
 * Overlay the server-authoritative display fields from the GraphQL scenario
 * views onto the full REST scenarios held in ScenarioContext, preserving the
 * server-provided order. This keeps card actions (workflow, park, delete —
 * which need permission/approval state) working while sourcing membership,
 * ordering and persisted metrics from the view. Scenarios not yet present in
 * the context (e.g. still loading) fall back to the view-only mapping.
 *
 * `appendContextOnly` controls whether scenarios that exist in the REST context
 * but not in the GraphQL view are appended. This surfaces newly-created
 * scenarios instantly before the view refetches, but MUST be disabled whenever a
 * server-side filter (search/category/status) is active — otherwise scenarios
 * the server deliberately excluded would be appended back, defeating the filter.
 */
export function enrichScenarioViews(
  viewScenarios: ComprehensiveScenario[],
  contextScenarios: ComprehensiveScenario[],
  options?: { appendContextOnly?: boolean },
): ComprehensiveScenario[] {
  const appendContextOnly = options?.appendContextOnly ?? true;
  const contextById = new Map(contextScenarios.map((s) => [s.id, s]));

  // Filter out scenarios that have been removed from the REST context (deleted)
  // but still linger in the stale GraphQL view until it refetches.
  const enriched = viewScenarios
    .filter((view) => contextById.has(view.id))
    .map((view) => {
      const full = contextById.get(view.id);
      if (!full) return view;
      const modifiedAt =
        full.modifiedAt > view.modifiedAt ? full.modifiedAt : view.modifiedAt;
      return {
        ...full,
        name: view.name,
        description: view.description,
        // Prefer context status — it reflects optimistic/recent mutations
        // before the GraphQL view refetches.
        status: full.status,
        priority: full.priority,
        label: view.label,
        category: full.category,
        createdByName: view.createdByName,
        modifiedAt,
        adjustmentCount: view.adjustmentCount,
        // vw_Scenarios is authoritative for the current user's access level.
        currentUserPermission: view.currentUserPermission,
        // vw_Scenarios is authoritative for explicit-vs-inherited access.
        isShared: view.isShared,
        isInherited: view.isInherited,
        // vw_Scenarios is authoritative for derived freshness.
        freshness: view.freshness,
        metrics: {
          ...full.metrics,
          minCash: view.metrics.minCash,
          runway: view.metrics.runway,
          netImpact: view.metrics.netImpact,
        },
      };
    });

  // Append scenarios that exist in context but not yet in the GraphQL view
  // (newly created scenarios before the view query refetches). Skipped when a
  // server-side filter is active so the filter result is authoritative.
  if (appendContextOnly) {
    const viewIds = new Set(viewScenarios.map((v) => v.id));
    for (const ctx of contextScenarios) {
      if (!viewIds.has(ctx.id)) {
        enriched.push(ctx);
      }
    }
  }

  return enriched;
}

/**
 * Live-mode data source for the Scenarios tabs. Fetches scenario cards from
 * GraphQL (vw_Scenarios) with combined server-side filtering + sorting.
 *
 * In demo mode this is a no-op — callers keep their existing client-side path.
 */
export function useScenarioViews(
  params: ScenarioViewParams,
): ScenarioViewResult {
  const { mode } = useApp();
  const isLive = mode === "live";
  const enabled = isLive && params.enabled !== false;

  const filter = useMemo(
    () => buildScenarioViewFilter(params),
    [params.search, params.category, params.status],
  );
  const sort = useMemo(
    () => [SORT_MAP[params.sortBy ?? "recent"]],
    [params.sortBy],
  );
  const first = params.first ?? 200;

  const listQuery = useQuery({
    queryKey: ["scenarioViews", filter, sort, first],
    queryFn: () => getScenariosGraphql(filter, first, undefined, sort),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const countQuery = useQuery({
    queryKey: ["scenarioViewsCount", filter],
    queryFn: () => getScenariosCount(filter),
    enabled,
    staleTime: 30_000,
  });

  const scenarios = useMemo(() => {
    if (!listQuery.data) return [];
    return listQuery.data.edges.map((edge) =>
      mapScenarioViewToScenario(edge.node),
    );
  }, [listQuery.data]);

  return {
    scenarios,
    totalCount: countQuery.data ?? scenarios.length,
    isLoading: enabled && (listQuery.isPending || countQuery.isPending),
    error:
      listQuery.error instanceof Error
        ? listQuery.error.message
        : listQuery.error
          ? "Failed to load scenarios"
          : null,
    isLive,
  };
}
